import React, { useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, formatTicketRef } from '../lib/api';
import {
  ArrowLeft, MapPin, QrCode, Download, Package, Edit, AlertCircle,
  Building2, X, Check, Plus, Search, ExternalLink, Move, ChevronRight,
  AlertTriangle, Printer, Map, Ticket, Clock, Upload, Camera, ImageIcon, Trash2
} from 'lucide-react';
import type { Asset, Location } from '../lib/api';
import { PriorityDot } from '../components/DataTable';
import { canPrint, printLocationLabel, isMobile } from '../lib/print';
import PCPrintButton, { ChangePrinterButton } from '../components/PCPrintButton';

// ============================================================================
// CONFIGURATIONS
// ============================================================================

const statusConfig: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  active: { label: 'Attivo', bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  in_repair: { label: 'In Riparazione', bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500' },
  in_maintenance: { label: 'In Manutenzione', bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
  in_transit: { label: 'In Transito', bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500' },
  in_storage: { label: 'In Magazzino', bg: 'bg-gray-100', text: 'text-gray-800', dot: 'bg-gray-500' },
  disposed: { label: 'Dismesso', bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
  lost: { label: 'Perso', bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' }
};

const conditionConfig: Record<string, { label: string; color: string; emoji: string }> = {
  excellent: { label: 'Eccellente', color: 'text-green-600', emoji: '✨' },
  good: { label: 'Buono', color: 'text-blue-600', emoji: '👍' },
  fair: { label: 'Discreto', color: 'text-yellow-600', emoji: '👌' },
  poor: { label: 'Scarso', color: 'text-orange-600', emoji: '⚠️' },
  broken: { label: 'Rotto', color: 'text-red-600', emoji: '❌' }
};

const typeLabels: Record<string, string> = {
  unit: 'Unità abitativa',
  area: 'Area',
  warehouse: 'Magazzino',
  common: 'Area comune',
  other: 'Altro'
};

// ============================================================================
// MODAL COMPONENT
// ============================================================================

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'default' | 'large';
}

function Modal({ isOpen, onClose, title, children, size = 'default' }: ModalProps) {
  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        <div className={`relative w-full ${size === 'large' ? 'max-w-3xl' : 'max-w-lg'} p-6 text-left bg-white rounded-2xl shadow-xl`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function LocationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addAssetsModalOpen, setAddAssetsModalOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  
  // Photo upload states
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [originalPhotoUrl, setOriginalPhotoUrl] = useState<string>('');

  // Form states
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    type: '',
    code: '',
    capacity: '',
    photoUrl: '',
    mapsLink: '',
    latitude: '',
    longitude: ''
  });

  // Asset selection for moving
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [moveCompleted, setMoveCompleted] = useState(true);
  const [assetSearchQuery, setAssetSearchQuery] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['location', id],
    queryFn: () => api.getLocation(id!),
    refetchOnMount: 'always'
  });

  // Get all available assets for moving (from other locations)
  const { data: allAssetsData } = useQuery({
    queryKey: ['assets-for-move'],
    queryFn: () => api.getAssets(),
    enabled: addAssetsModalOpen
  });

  const updateMutation = useMutation({
    mutationFn: (updateData: any) => api.updateLocation(id!, updateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location', id] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setEditModalOpen(false);
    }
  });

  const moveAssetsMutation = useMutation({
    mutationFn: (data: { assetIds: string[]; completed: boolean }) =>
      api.moveAssetsToLocation(id!, data.assetIds, data.completed),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location', id] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['assets-for-move'] });
      setAddAssetsModalOpen(false);
      setSelectedAssets([]);
      setMoveCompleted(true);
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Caricamento ubicazione...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 text-red-700 p-6 rounded-2xl border border-red-100">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">Errore: {error?.message || 'Ubicazione non trovata'}</span>
        </div>
        <Link to="/locations" className="mt-4 btn btn-secondary inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Torna alle ubicazioni
        </Link>
      </div>
    );
  }

  const { location, assets: locationAssets, children, tickets: locationTickets } = data;
  
  // Use location code if available, otherwise show informative message
  const qrCodeData = location.code || `LOC-${location.id.slice(0, 8).toUpperCase()}`;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCodeData)}`;

  // Filter available assets: from other locations and without open tickets
  const availableAssets = allAssetsData?.assets.filter(asset => {
    // Exclude assets already in this location
    if (asset.locationId === id) return false;
    // Only include assets without open tickets (we'll filter this on the server side too)
    return true;
  }) || [];

  // Filter by search query
  const filteredAvailableAssets = availableAssets.filter(asset =>
    asset.name.toLowerCase().includes(assetSearchQuery.toLowerCase()) ||
    asset.qrCode.toLowerCase().includes(assetSearchQuery.toLowerCase())
  );

  const handleOpenEditModal = () => {
    const photoUrl = location.photoUrl || '';
    setEditForm({
      name: location.name || '',
      description: location.description || '',
      type: location.type || 'other',
      code: location.code || '',
      capacity: location.capacity?.toString() || '',
      photoUrl,
      mapsLink: location.mapsLink || '',
      latitude: location.latitude?.toString() || '',
      longitude: location.longitude?.toString() || ''
    });
    setOriginalPhotoUrl(photoUrl);
    setUploadError(null);
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    // If photo was removed or changed, delete the old one from storage
    if (originalPhotoUrl && 
        originalPhotoUrl !== editForm.photoUrl && 
        originalPhotoUrl.startsWith('/api/files/')) {
      const key = originalPhotoUrl.replace('/api/files/', '');
      try {
        await fetch('/api/upload', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key }),
          credentials: 'include'
        });
      } catch (error) {
        console.error('Error deleting old photo:', error);
      }
    }
    
    updateMutation.mutate({
      name: editForm.name,
      description: editForm.description || null,
      type: editForm.type,
      code: editForm.code || null,
      capacity: editForm.capacity ? parseInt(editForm.capacity) : null,
      photoUrl: editForm.photoUrl || null,
      mapsLink: editForm.mapsLink || null,
      latitude: editForm.latitude ? parseFloat(editForm.latitude) : null,
      longitude: editForm.longitude ? parseFloat(editForm.longitude) : null
    });
  };

  const handlePhotoUpload = async (file: File) => {
    setIsUploading(true);
    setUploadError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', 'location');
      formData.append('entityId', id!);
      
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json() as { error?: string };
        throw new Error(errorData.error || 'Errore durante il caricamento');
      }
      
      const data = await response.json() as { url: string };
      setEditForm(prev => ({ ...prev, photoUrl: data.url }));
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Errore durante il caricamento');
    } finally {
      setIsUploading(false);
    }
  };
  
  const handlePhotoRemove = () => {
    // Remove from state only (don't delete from storage yet)
    setEditForm(prev => ({ ...prev, photoUrl: '' }));
  };
  
  const handleCancelEdit = async () => {
    // If user uploaded a new photo but cancels, delete it
    if (editForm.photoUrl && editForm.photoUrl !== originalPhotoUrl && editForm.photoUrl.startsWith('/api/files/')) {
      const key = editForm.photoUrl.replace('/api/files/', '');
      try {
        await fetch('/api/upload', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key }),
          credentials: 'include'
        });
      } catch (error) {
        console.error('Cleanup error:', error);
      }
    }
    setEditModalOpen(false);
  };

  const handleMoveAssets = () => {
    if (selectedAssets.length === 0) return;
    moveAssetsMutation.mutate({
      assetIds: selectedAssets,
      completed: moveCompleted
    });
  };

  const toggleAssetSelection = (assetId: string) => {
    setSelectedAssets(prev =>
      prev.includes(assetId)
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const formatDate = (date: string | number | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Code - ${location.name}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            text-align: center;
            padding: 40px;
          }
          .qr-container {
            display: inline-block;
            border: 2px solid #333;
            padding: 20px;
            border-radius: 12px;
          }
          .qr-code {
            width: 200px;
            height: 200px;
          }
          .location-name {
            font-size: 18px;
            font-weight: bold;
            margin-top: 15px;
            color: #333;
          }
          .location-code {
            font-size: 12px;
            color: #666;
            margin-top: 5px;
            font-family: monospace;
          }
          .location-type {
            font-size: 11px;
            color: #888;
            margin-top: 5px;
          }
          @media print {
            body { padding: 20px; }
          }
        </style>
      </head>
      <body>
        <div class="qr-container">
          <img src="${qrCodeUrl}" alt="QR Code" class="qr-code" />
          <div class="location-name">${location.name}</div>
          <div class="location-code">${qrCodeData}</div>
          <div class="location-type">${typeLabels[location.type] || location.type}</div>
        </div>
        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link to="/locations" className="icon-btn mt-1">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full">
                {typeLabels[location.type] || location.type}
              </span>
              {location.code && (
                <span className="text-xs font-mono text-gray-500">{location.code}</span>
              )}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{location.name}</h1>
            {location.description && (
              <p className="text-gray-500 mt-1">{location.description}</p>
            )}
          </div>
        </div>
        <button
          onClick={handleOpenEditModal}
          className="btn btn-secondary flex items-center gap-2"
        >
          <Edit className="w-4 h-4" />
          Modifica
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Location Image */}
          {location.photoUrl ? (
            <div className="card p-0 overflow-hidden bg-gray-50">
              <img
                src={location.photoUrl}
                alt={location.name}
                className="w-full max-h-80 object-contain mx-auto"
              />
            </div>
          ) : (
            <div className="card">
              <div className="bg-gray-100 rounded-xl h-48 flex items-center justify-center">
                <Building2 className="w-16 h-16 text-gray-300" />
              </div>
            </div>
          )}

          {/* Location Details */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Dettagli</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-gray-500 mb-1">Tipo</dt>
                <dd className="flex items-center gap-2 text-gray-900">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  {typeLabels[location.type] || location.type}
                </dd>
              </div>
              {location.capacity && (
                <div>
                  <dt className="text-sm text-gray-500 mb-1">Capacità</dt>
                  <dd className="text-gray-900">{location.capacity} posti</dd>
                </div>
              )}
              <div>
                <dt className="text-sm text-gray-500 mb-1">Asset presenti</dt>
                <dd className="flex items-center gap-2 text-gray-900">
                  <Package className="w-4 h-4 text-gray-400" />
                  {locationAssets?.length || 0}
                </dd>
              </div>
              {location.createdAt && (
                <div>
                  <dt className="text-sm text-gray-500 mb-1">Creato il</dt>
                  <dd className="text-gray-900">{formatDate(location.createdAt)}</dd>
                </div>
              )}
            </div>

            {/* Maps Link */}
            {(location.mapsLink || (location.latitude && location.longitude)) && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Map className="w-4 h-4" />
                  Posizione
                </h3>
                {location.latitude && location.longitude && (
                  <p className="text-sm text-gray-600 mb-2">
                    Coordinate: {location.latitude}, {location.longitude}
                  </p>
                )}
                {location.mapsLink && (
                  <a
                    href={location.mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Apri in Google Maps
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Assets List */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-gray-400" />
                Asset in questa ubicazione
                {locationAssets && locationAssets.length > 0 && (
                  <span className="text-sm font-normal text-gray-500">({locationAssets.length})</span>
                )}
              </h2>
              <button
                onClick={() => setAddAssetsModalOpen(true)}
                className="btn btn-primary btn-sm"
              >
                <Plus className="w-4 h-4" />
                Aggiungi
              </button>
            </div>

            {(!locationAssets || locationAssets.length === 0) ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500">Nessun asset in questa ubicazione</p>
                <button
                  onClick={() => setAddAssetsModalOpen(true)}
                  className="btn btn-secondary mt-4 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Sposta asset qui
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Codice</th>
                      <th>Nome</th>
                      <th>Stato</th>
                      <th className="hidden sm:table-cell">Categoria</th>
                      <th className="hidden sm:table-cell">Condizione</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {locationAssets.map((asset: any) => {
                      const assetStatus = statusConfig[asset.status] || statusConfig.active;
                      const assetCondition = conditionConfig[asset.condition] || conditionConfig.good;
                      return (
                        <tr
                          key={asset.id}
                          className="clickable"
                          onClick={() => navigate(`/assets/${asset.id}`)}
                        >
                          <td className="font-mono text-xs text-gray-500">{asset.qrCode}</td>
                          <td className="font-medium text-gray-900">{asset.name}</td>
                          <td>
                            <span className={`badge ${assetStatus.bg} ${assetStatus.text}`}>
                              {assetStatus.label}
                            </span>
                          </td>
                          <td className="hidden sm:table-cell text-sm text-gray-600">
                            {asset.category?.name || '-'}
                          </td>
                          <td className="hidden sm:table-cell">
                            <span className={assetCondition.color}>{assetCondition.emoji} {assetCondition.label}</span>
                          </td>
                          <td>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Child Locations */}
          {children && children.length > 0 && (
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-gray-400" />
                Sotto-ubicazioni
                <span className="text-sm font-normal text-gray-500">({children.length})</span>
              </h2>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Tipo</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {children.map((child: any) => (
                      <tr
                        key={child.id}
                        className="clickable"
                        onClick={() => navigate(`/locations/${child.id}`)}
                      >
                        <td className="font-medium text-gray-900">{child.name}</td>
                        <td className="text-sm text-gray-600">{typeLabels[child.type] || child.type}</td>
                        <td>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Ticket Correlati - Enterprise Table Style */}
          {data.ticketingEnabled && (
            <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                <Ticket className="w-4 h-4 text-orange-500" />
                Ticket Correlati
                {locationTickets && locationTickets.length > 0 && (
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full text-xs font-bold">
                    {locationTickets.length}
                  </span>
                )}
              </h2>
              <Link
                to={`/tickets/new?location=${id}`}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                + Nuovo
              </Link>
            </div>

            {!locationTickets || locationTickets.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto bg-gray-100 rounded-xl flex items-center justify-center mb-3">
                  <Ticket className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-gray-500 text-sm">Nessun ticket per questa ubicazione</p>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase w-6"></th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Rif.</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Titolo</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Stato</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Data</th>
                      <th className="py-2 px-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {locationTickets.map((ticket: any) => {
                      const ticketStatusConfig: Record<string, { label: string; bg: string; text: string }> = {
                        open: { label: 'Aperto', bg: 'bg-amber-100', text: 'text-amber-700' },
                        in_progress: { label: 'In Corso', bg: 'bg-blue-100', text: 'text-blue-700' },
                        waiting: { label: 'In Attesa', bg: 'bg-orange-100', text: 'text-orange-700' },
                        resolved: { label: 'Risolto', bg: 'bg-green-100', text: 'text-green-700' },
                        closed: { label: 'Chiuso', bg: 'bg-gray-100', text: 'text-gray-700' }
                      };
                      const st = ticketStatusConfig[ticket.status] || ticketStatusConfig.open;
                      const formatDate = (ts: number) => new Date(ts).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
                      return (
                        <tr 
                          key={ticket.id}
                          onClick={() => navigate(`/tickets/${ticket.id}`)}
                          className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="py-2.5 px-3">
                            <PriorityDot priority={ticket.priority} />
                          </td>
                          <td className="py-2.5 px-3">
                            <span className="font-mono text-xs text-gray-500">{formatTicketRef(ticket.ticketNumber)}</span>
                          </td>
                          <td className="py-2.5 px-3 max-w-[200px]">
                            <span className="font-medium text-gray-900 truncate block">{ticket.title}</span>
                          </td>
                          <td className="py-2.5 px-3">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>
                              {st.label}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 hidden sm:table-cell">
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(ticket.createdAt)}
                            </span>
                          </td>
                          <td className="py-2.5 px-2">
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Sidebar - Right Column */}
        <div className="flex flex-col space-y-6">
          {/* Quick Actions - First on mobile, second on desktop */}
          <div className="card order-1 lg:order-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Azioni Rapide</h2>
            <div className="space-y-2">
              <button
                onClick={() => setAddAssetsModalOpen(true)}
                className="w-full btn flex items-center justify-center gap-2 bg-green-100 hover:bg-green-200 text-green-700 border-green-200"
              >
                <Move className="w-4 h-4" />
                Sposta Asset Qui
              </button>
              <Link
                to={`/assets/new?location=${id}`}
                className="w-full btn flex items-center justify-center gap-2 bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-200"
              >
                <Plus className="w-4 h-4" />
                Nuovo Asset
              </Link>
            </div>
          </div>

          {/* QR Code - Second on mobile, first on desktop */}
          <div className="card order-2 lg:order-1">
            <div className="flex items-center gap-2 mb-4">
              <QrCode className="w-4 h-4 text-primary-500" />
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Codice QR</h2>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-white p-3 rounded-xl border-2 border-gray-100 shadow-sm">
                <img
                  src={qrCodeUrl}
                  alt={`QR Code ${location.name}`}
                  className="w-44 h-44"
                />
              </div>
              <p className="text-gray-600 font-mono text-xs mt-3 px-3 py-1.5 bg-gray-100 rounded-lg">{qrCodeData}</p>
              <div className="flex gap-2 w-full mt-3">
                <a
                  href={qrCodeUrl}
                  download={`location-${location.id}.png`}
                  className="btn btn-secondary text-sm flex-1 flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Scarica
                </a>
                {canPrint() ? (
                  <button
                    onClick={() => {
                      printLocationLabel({
                        code: qrCodeData,
                        qrUrl: qrCodeData,
                        name: location.name,
                        locationType: typeLabels[location.type] || location.type,
                        capacity: location.capacity ? `${location.capacity} posti` : undefined,
                        hasChildren: children && children.length > 0,
                      });
                    }}
                    className="btn btn-primary text-sm flex-1 flex items-center justify-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Stampa Etichetta
                  </button>
                ) : !isMobile() ? (
                  <PCPrintButton
                    type="location"
                    data={{
                      code: qrCodeData,
                      qrContent: qrCodeData,
                      name: location.name,
                      locationType: typeLabels[location.type] || location.type,
                      capacity: location.capacity ? `${location.capacity} posti` : undefined,
                      hasChildren: children && children.length > 0,
                    }}
                  />
                ) : (
                  <button
                    onClick={handlePrint}
                    className="btn btn-secondary text-sm flex-1 flex items-center justify-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Stampa
                  </button>
                )}
              </div>
              {canPrint() && (
                <button
                  onClick={handlePrint}
                  className="text-xs text-gray-500 hover:text-gray-700 mt-2 underline cursor-pointer"
                >
                  Stampa da browser
                </button>
              )}
              {!isMobile() && <ChangePrinterButton />}
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================================
          MODALS
          ============================================================================ */}

      {/* Edit Modal */}
      <Modal isOpen={editModalOpen} onClose={handleCancelEdit} title="Modifica Ubicazione">
        <div className="space-y-4">
          <div className="form-group">
            <label className="form-label">
              Nome <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              className="input"
              placeholder="es. Piscina principale"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Descrizione</label>
            <textarea
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              className="input"
              rows={2}
              placeholder="Descrizione opzionale..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">Tipo</label>
              <select
                value={editForm.type}
                onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                className="input"
              >
                <option value="unit">Unità abitativa</option>
                <option value="area">Area</option>
                <option value="warehouse">Magazzino</option>
                <option value="common">Area comune</option>
                <option value="other">Altro</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Codice</label>
              <input
                type="text"
                value={editForm.code}
                onChange={(e) => setEditForm({ ...editForm, code: e.target.value })}
                className="input"
                placeholder="es. A-001"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Capacità</label>
            <input
              type="number"
              value={editForm.capacity}
              onChange={(e) => setEditForm({ ...editForm, capacity: e.target.value })}
              className="input"
              placeholder="Numero di posti"
            />
          </div>

          {/* Photo Upload Section */}
          <div className="form-group">
            <label className="form-label">Foto</label>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handlePhotoUpload(file);
                e.target.value = '';
              }}
              className="hidden"
            />
            
            {editForm.photoUrl ? (
              <div className="relative">
                <img
                  src={editForm.photoUrl}
                  alt="Foto ubicazione"
                  className="w-full h-48 object-cover rounded-xl border border-gray-200"
                />
                <button
                  type="button"
                  onClick={handlePhotoRemove}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                {isUploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-8 h-8 border-3 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                    <span className="text-sm text-gray-500">Caricamento...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <ImageIcon className="w-10 h-10 text-gray-400" />
                    <p className="text-sm text-gray-500">Nessuna foto</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="btn btn-secondary text-sm flex items-center gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        Carica
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.capture = 'environment';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) handlePhotoUpload(file);
                          };
                          input.click();
                        }}
                        className="btn btn-secondary text-sm flex items-center gap-2"
                      >
                        <Camera className="w-4 h-4" />
                        Scatta
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {uploadError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-700 font-medium text-sm">Errore nel caricamento</p>
                  <p className="text-red-600 text-sm">{uploadError}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setUploadError(null)}
                  className="ml-auto p-1 text-red-400 hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Link Google Maps</label>
            <input
              type="url"
              value={editForm.mapsLink}
              onChange={(e) => setEditForm({ ...editForm, mapsLink: e.target.value })}
              className="input"
              placeholder="https://maps.google.com/..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">Latitudine</label>
              <input
                type="text"
                value={editForm.latitude}
                onChange={(e) => setEditForm({ ...editForm, latitude: e.target.value })}
                className="input"
                placeholder="es. 45.4654"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Longitudine</label>
              <input
                type="text"
                value={editForm.longitude}
                onChange={(e) => setEditForm({ ...editForm, longitude: e.target.value })}
                className="input"
                placeholder="es. 9.1866"
              />
            </div>
          </div>

          {updateMutation.error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {updateMutation.error.message}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleCancelEdit}
              className="flex-1 btn btn-secondary"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleSaveEdit}
              disabled={!editForm.name.trim() || updateMutation.isPending}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Assets Modal */}
      <Modal
        isOpen={addAssetsModalOpen}
        onClose={() => {
          setAddAssetsModalOpen(false);
          setSelectedAssets([]);
          setAssetSearchQuery('');
          setMoveCompleted(true);
        }}
        title="Sposta Asset in questa ubicazione"
        size="large"
      >
        <div className="space-y-4">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">Nota importante</p>
                <p className="mt-1">
                  Sono mostrati solo gli asset che <strong>non hanno ticket aperti</strong>.
                  Gli asset con ticket in gestione devono essere spostati tramite il ticket.
                </p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca asset per nome o codice QR..."
              value={assetSearchQuery}
              onChange={(e) => setAssetSearchQuery(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Assets List */}
          <div className="border border-gray-200 rounded-xl max-h-64 overflow-y-auto">
            {filteredAvailableAssets.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Package className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p>Nessun asset disponibile per lo spostamento</p>
                <p className="text-sm mt-1">Tutti gli asset hanno ticket aperti o sono già in questa ubicazione</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredAvailableAssets.map((asset) => {
                  const isSelected = selectedAssets.includes(asset.id);
                  const hasOpenTicket = (asset as any).hasOpenTicket;

                  return (
                    <label
                      key={asset.id}
                      className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                        isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                      } ${hasOpenTicket ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => !hasOpenTicket && toggleAssetSelection(asset.id)}
                        disabled={hasOpenTicket}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 truncate">{asset.name}</span>
                          {hasOpenTicket && (
                            <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
                              Ticket aperto
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-400 font-mono">{asset.qrCode}</span>
                          {asset.location && (
                            <span className="text-xs text-gray-500">
                              • da {asset.location.name}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        statusConfig[asset.status]?.bg || 'bg-gray-100'
                      } ${statusConfig[asset.status]?.text || 'text-gray-700'}`}>
                        {statusConfig[asset.status]?.label || asset.status}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected count */}
          {selectedAssets.length > 0 && (
            <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-primary-700">
                  {selectedAssets.length} asset selezionati
                </span>
              </div>

              {/* Move completed checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={moveCompleted}
                  onChange={(e) => setMoveCompleted(e.target.checked)}
                  className="w-4 h-4 mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <span className="font-medium text-gray-900">Lo spostamento è già completato</span>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {moveCompleted
                      ? 'Gli asset verranno impostati come "Attivi" nella nuova ubicazione'
                      : 'Gli asset verranno impostati come "In Transito" fino al completamento'
                    }
                  </p>
                </div>
              </label>
            </div>
          )}

          {moveAssetsMutation.error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {moveAssetsMutation.error.message}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setAddAssetsModalOpen(false);
                setSelectedAssets([]);
                setAssetSearchQuery('');
                setMoveCompleted(true);
              }}
              className="flex-1 btn btn-secondary"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleMoveAssets}
              disabled={selectedAssets.length === 0 || moveAssetsMutation.isPending}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {moveAssetsMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Spostamento...
                </>
              ) : (
                <>
                  <Move className="w-4 h-4" />
                  Sposta {selectedAssets.length > 0 ? `(${selectedAssets.length})` : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
